import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Spotlights each step's target element (a ref to a DOM node already
// present in the page) with a dark scrim + cutout and a tooltip explaining
// what it does, advancing on "Next". Mirrors the mobile app's
// WalkthroughOverlay/WalkthroughStep, adapted for the DOM: `target` is a
// React ref instead of a GlobalKey, measured via getBoundingClientRect
// instead of a RenderBox.
//
// A step whose target isn't currently visible (unmounted, or hidden by a
// responsive `display: none` — e.g. the desktop-only nav controls this is
// built for don't render at all on narrow viewports) is skipped
// automatically rather than getting the tour stuck, so the whole thing
// degrades to a silent no-op instead of a broken overlay on small screens.
export default function WalkthroughOverlay({ active, steps, onFinished }) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);

  const measure = useCallback(() => {
    if (!active) return;
    const step = steps[index];
    if (!step) return;
    const el = step.target?.current;
    const box = el?.getBoundingClientRect();
    if (!box || box.width === 0 || box.height === 0) {
      // Target not visible at this viewport — skip to the next step, or
      // finish the tour if this was the last one.
      if (index < steps.length - 1) setIndex((i) => i + 1);
      else onFinished('skipped');
      return;
    }
    setRect(box);
  }, [active, index, steps, onFinished]);

  // `index`/`rect` already start at their correct reset values (0/null)
  // from useState, and `active` only ever transitions false→true once per
  // page session (the caller never re-shows a finished tour) — so measuring
  // is the only thing this effect needs to do. An earlier version also had
  // a second effect that reset index/rect on every `active` change; on the
  // very same activation, effect ordering meant it ran right after this one
  // and wiped out the measurement it had just taken, so the tour never
  // rendered anything at all. Do not reintroduce that without merging the
  // reset into this same effect first.
  useEffect(() => {
    if (!active) return undefined;
    measure();
    const onChange = () => measure();
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, index]);

  if (!active || !rect) return null;
  const step = steps[index];
  const lastStep = index === steps.length - 1;

  function next() {
    if (lastStep) { onFinished('completed'); return; }
    setIndex((i) => i + 1);
    setRect(null);
  }

  const spaceBelow = window.innerHeight - rect.bottom;
  const showBelow = spaceBelow >= rect.top;

  return (
    <div className="fixed inset-0 z-[100]">
      <div
        className="absolute rounded-2xl ring-2 ring-white transition-all duration-200"
        style={{
          top: rect.top - 6,
          left: rect.left - 6,
          width: rect.width + 12,
          height: rect.height + 12,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.72)',
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={step.title}
        className="absolute mx-4 max-w-sm rounded-2xl bg-white p-5 shadow-2xl sm:mx-0"
        style={{
          left: Math.max(16, Math.min(rect.left, window.innerWidth - 400)),
          top: showBelow ? rect.bottom + 16 : undefined,
          bottom: showBelow ? undefined : window.innerHeight - rect.top + 16,
        }}
      >
        <p className="text-base font-black text-slate-900">{step.title}</p>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{step.description}</p>
        <div className="mt-4 flex items-center">
          <div className="flex gap-1">
            {steps.map((s, i) => (
              <span
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                className={`h-1.5 rounded-full transition-all ${i === index ? 'w-4 bg-indigo-600' : 'w-1.5 bg-slate-200'}`}
              />
            ))}
          </div>
          <div className="ml-auto flex items-center gap-3">
            <button type="button" onClick={() => onFinished('skipped')} className="text-sm font-semibold text-slate-500 hover:text-slate-700">
              {t('walkthrough.skip')}
            </button>
            <button type="button" onClick={next} className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700">
              {lastStep ? t('walkthrough.gotIt') : t('walkthrough.next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
